import { GitMerge, GitPullRequest, GitPullRequestClosed } from 'lucide-react';

import { Badge } from '@/_shared/components/ui/badge';
import { Card, CardContent } from '@/_shared/components/ui/card';

import type { MeasurementPlanPr } from '../types';

interface PRsSectionProps {
  prs: MeasurementPlanPr[];
}

function statusIcon(status: MeasurementPlanPr['status']): React.ReactElement {
  if (status === 'merged') return <GitMerge className="size-4 text-purple-600" aria-label="Merged" />;
  if (status === 'closed') return <GitPullRequestClosed className="size-4 text-rose-600" aria-label="Closed" />;
  return <GitPullRequest className="size-4 text-emerald-600" aria-label="Open" />;
}

export function PRsSection({ prs }: PRsSectionProps): React.ReactElement {
  if (prs.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          No pull requests linked yet.
        </CardContent>
      </Card>
    );
  }

  const grouped = new Map<string, MeasurementPlanPr[]>();
  for (const pr of prs) {
    const list = grouped.get(pr.repoFullName) ?? [];
    list.push(pr);
    grouped.set(pr.repoFullName, list);
  }

  return (
    <div className="space-y-4">
      {Array.from(grouped.entries()).map(([repo, repoPrs]) => (
        <Card key={repo}>
          <CardContent className="py-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-mono text-sm text-foreground">{repo}</h4>
              <Badge variant="outline" className="text-[0.625rem]">
                {repoPrs.length} {repoPrs.length === 1 ? 'PR' : 'PRs'}
              </Badge>
            </div>
            <ul className="divide-y divide-border/40">
              {repoPrs.map((pr) => (
                <li key={pr.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                  {statusIcon(pr.status)}
                  <a
                    href={pr.prUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 text-sm text-foreground hover:underline"
                  >
                    #{pr.prNumber} {pr.title ?? 'Pull request'}
                  </a>
                  <Badge variant="outline" className="text-[0.625rem]">
                    {pr.status}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
